import {makeAutoObservable} from 'mobx';
import AnalyticsRepository from '../../../data/repositories/AnalyticsRepository';
import AuthRepository from '../../../data/repositories/AuthRepository';
import {
  normalizeStudioCostReport,
  type StudioCostQuery,
  type StudioCostReport,
} from '../../../data/models/StudioCosts';

/** Why the Studio cost report could not be shown. */
export type StudioCostsError =
  | 'forbidden'
  | 'unavailable'
  | 'invalid'
  | 'failed';

export type StudioCostsResult =
  | {report: StudioCostReport; error?: undefined; message?: undefined}
  | {report: null; error: StudioCostsError; message?: string};

/** A failure's own words: a string, or the `error` or `message` of the server's answer. */
function errorText(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (!detail || typeof detail !== 'object') return undefined;
  const {error, message} = detail as {error?: unknown; message?: unknown};
  const text = error ?? message;
  return typeof text === 'string' ? text : undefined;
}

class AdminPanelStore {
  private analyticsRepo = new AnalyticsRepository();
  private authRepo = new AuthRepository();

  constructor() {
    makeAutoObservable(this);
  }

  public getAnalytics() {
    return this.analyticsRepo.getAdminAnalytics();
  }

  public async getLastAccessData(page: number, pageSize: number) {
    const res = await this.analyticsRepo.getLastAccessData(page, pageSize);
    if (res.success && res.data) {
      return res.data;
    }
    return {
      data: [],
      pagesCount: 0,
    };
  }

  public async getRecentSignups(page: number, pageSize: number) {
    const res = await this.analyticsRepo.getRecentSignups(page, pageSize);
    if (res.success && res.data) {
      return res.data;
    }
    return {
      data: [],
      pagesCount: 0,
    };
  }

  /** Studio's provider and compute costs for a period; never throws. */
  public async getStudioCosts(
    query: StudioCostQuery,
  ): Promise<StudioCostsResult> {
    try {
      const res = await this.analyticsRepo.getStudioCosts(query);
      const report = res.success ? normalizeStudioCostReport(res.data) : null;
      if (report) return {report};
      const status = res.status ?? 0;
      if (status === 401 || status === 403)
        return {report: null, error: 'forbidden'};
      // An API without the cost report yet.
      if (status === 404 || status === 405)
        return {report: null, error: 'unavailable'};
      // The server says what is wrong with the filters, e.g. a range over 92 days.
      if (status === 400)
        return {
          report: null,
          error: 'invalid',
          message: errorText(res.errors?.[0]),
        };
      return {report: null, error: 'failed'};
    } catch {
      return {report: null, error: 'failed'};
    }
  }

  public logout() {
    return this.authRepo.logout();
  }
}

export default AdminPanelStore;
